import { useCallback, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { passengerAdmin, getAdminConfigWarning, asAdminList } from "../components/services/adminBackendFetch";
import { useAppContext } from "../providers/ProviderAppContainer";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { AdminTableShell } from "../components/AdminTableShell";

const DISCOUNT_TYPES = [
    { label: "Flat (CAD)", value: "flat" },
    { label: "Percentage", value: "percentage" },
];

function emptyForm() {
    const today = localDateOnly(0);
    return {
        code: "",
        start_date: today,
        end_date: localDateOnly(30),
        active: true,
        discount_type: "flat",
        discount_value: 20,
        upto_rides: null,
        upto_unlimited: true,
    };
}

function localDateOnly(offsetDays = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function rowToForm(row) {
    const start = String(row?.start_date || "").slice(0, 10);
    const end = String(row?.end_date || "").slice(0, 10);
    return {
        code: row?.code ?? "",
        start_date: /^\d{4}-\d{2}-\d{2}$/.test(start) ? start : localDateOnly(0),
        end_date: /^\d{4}-\d{2}-\d{2}$/.test(end) ? end : localDateOnly(30),
        active: !!row?.active,
        discount_type: row?.discount_type === "percentage" ? "percentage" : "flat",
        discount_value: Number(row?.discount_value) || 0,
        upto_rides: row?.upto_rides == null ? null : Number(row.upto_rides),
        upto_unlimited: row?.upto_rides == null,
    };
}

export default function CouponsPage() {
    const { showToast } = useAppContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm());

    const reload = useCallback(() => {
        const warn = getAdminConfigWarning();
        if (warn) {
            showToast({ severity: "warn", summary: "Configuration", detail: warn });
            setLoading(false);
            return Promise.resolve();
        }
        setLoading(true);
        return passengerAdmin("/admin/coupons")
            .then((data) => setRows(asAdminList(data)))
            .catch((e) => {
                showToast({
                    severity: "error",
                    summary: "Could not load coupons",
                    detail: String(e.message || e),
                });
                setRows([]);
            })
            .finally(() => setLoading(false));
    }, [showToast]);

    useEffect(() => {
        reload();
    }, [reload]);

    function openCreate() {
        setEditId(null);
        setForm(emptyForm());
        setDialogOpen(true);
    }

    function openEdit(row) {
        setEditId(row.id);
        setForm(rowToForm(row));
        setDialogOpen(true);
    }

    async function save() {
        const code = String(form.code || "").trim().toUpperCase();
        if (!code) {
            showToast({ severity: "warn", summary: "Coupon code required" });
            return;
        }
        if (!form.start_date || !form.end_date) {
            showToast({ severity: "warn", summary: "Start and end dates are required" });
            return;
        }
        if (form.end_date < form.start_date) {
            showToast({
                severity: "warn",
                summary: "Invalid dates",
                detail: "End date must be on or after start date.",
            });
            return;
        }
        if (!(Number(form.discount_value) > 0)) {
            showToast({ severity: "warn", summary: "Discount value must be greater than 0" });
            return;
        }
        if (form.discount_type === "percentage" && Number(form.discount_value) > 100) {
            showToast({
                severity: "warn",
                summary: "Invalid percentage",
                detail: "Percentage discount cannot exceed 100.",
            });
            return;
        }
        if (!/^[A-Z0-9_-]{2,50}$/i.test(code)) {
            showToast({
                severity: "warn",
                summary: "Invalid code",
                detail: "Use letters, numbers, _ or - (2–50 chars).",
            });
            return;
        }

        const payload = {
            code,
            start_date: form.start_date,
            end_date: form.end_date,
            active: !!form.active,
            discount_type: form.discount_type,
            discount_value: Number(form.discount_value),
            upto_rides: form.upto_unlimited ? null : Number(form.upto_rides) || null,
        };
        if (!form.upto_unlimited && !(Number(form.upto_rides) >= 1)) {
            showToast({
                severity: "warn",
                summary: "upto_rides",
                detail: "Enter a ride limit (e.g. 3) or enable Unlimited.",
            });
            return;
        }

        setSaving(true);
        try {
            if (editId) {
                await passengerAdmin(`/admin/coupons/${editId}`, {
                    method: "PATCH",
                    body: JSON.stringify(payload),
                });
                showToast({ severity: "success", summary: "Coupon updated", detail: code });
            } else {
                await passengerAdmin("/admin/coupons", {
                    method: "POST",
                    body: JSON.stringify(payload),
                });
                showToast({ severity: "success", summary: "Coupon created", detail: code });
            }
            setDialogOpen(false);
            await reload();
        } catch (e) {
            showToast({
                severity: "error",
                summary: "Save failed",
                detail: String(e.message || e),
            });
        } finally {
            setSaving(false);
        }
    }

    function requestDelete(row) {
        confirmDialog({
            message: `Delete coupon ${row.code}? This cannot be undone.`,
            header: "Delete coupon",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
                try {
                    await passengerAdmin(`/admin/coupons/${row.id}`, { method: "DELETE" });
                    showToast({ severity: "success", summary: "Deleted", detail: row.code });
                    await reload();
                } catch (e) {
                    showToast({
                        severity: "error",
                        summary: "Delete failed",
                        detail: String(e.message || e),
                    });
                }
            },
        });
    }

    const typeBody = (row) =>
        row.discount_type === "percentage" ? (
            <Tag value={`${Number(row.discount_value)}%`} severity="info" />
        ) : (
            <Tag value={`$${Number(row.discount_value).toFixed(2)} flat`} severity="success" />
        );

    const activeBody = (row) =>
        row.active ? <Tag value="Active" severity="success" /> : <Tag value="Inactive" severity="danger" />;

    const uptoBody = (row) => (row.upto_rides == null ? "Unlimited" : String(row.upto_rides));

    const actionsBody = (row) => (
        <div className="uc-admin-table-actions">
            <Button label="Edit" icon="pi pi-pencil" size="small" outlined onClick={() => openEdit(row)} />
            <Button
                label="Delete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                outlined
                onClick={() => requestDelete(row)}
            />
        </div>
    );

    return (
        <div className="uc-admin-page">
            <ConfirmDialog />
            <AdminPageHeader
                title="Coupons"
                subtitle="Create promo codes with flat or percentage discounts and optional first-N-rides limits."
                actions={
                    <Button label="New coupon" icon="pi pi-plus" onClick={openCreate} />
                }
            />

            {loading ? (
                <div className="flex justify-content-center p-5">
                    <ProgressSpinner />
                </div>
            ) : (
                <AdminTableShell>
                    <DataTable value={rows} emptyMessage="No coupons yet. Create FLAT20 or FLAT30 to get started." paginator rows={20}>
                        <Column field="code" header="Code" sortable />
                        <Column header="Discount" body={typeBody} />
                        <Column field="start_date" header="Start" />
                        <Column field="end_date" header="End" />
                        <Column header="Upto rides" body={uptoBody} />
                        <Column header="Status" body={activeBody} />
                        <Column header="" body={actionsBody} style={{ width: "12rem" }} />
                    </DataTable>
                </AdminTableShell>
            )}

            <Dialog
                header={editId ? "Edit coupon" : "Create coupon"}
                visible={dialogOpen}
                style={{ width: "min(32rem, 95vw)" }}
                onHide={() => setDialogOpen(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" className="p-button-text" onClick={() => setDialogOpen(false)} />
                        <Button label={editId ? "Save" : "Create"} loading={saving} onClick={save} />
                    </div>
                }
            >
                <div className="flex flex-column gap-3 pt-2">
                    <div className="flex flex-column gap-1">
                        <label htmlFor="coupon-code">Coupon code</label>
                        <InputText
                            id="coupon-code"
                            value={form.code}
                            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                            placeholder="SUPER20"
                        />
                    </div>
                    <div className="grid">
                        <div className="col-6 flex flex-column gap-1">
                            <label htmlFor="coupon-start">Start date</label>
                            <InputText
                                id="coupon-start"
                                type="date"
                                value={form.start_date}
                                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                            />
                        </div>
                        <div className="col-6 flex flex-column gap-1">
                            <label htmlFor="coupon-end">End date</label>
                            <InputText
                                id="coupon-end"
                                type="date"
                                value={form.end_date}
                                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <InputSwitch
                            checked={form.active}
                            onChange={(e) => setForm((f) => ({ ...f, active: e.value }))}
                        />
                        <span>Active</span>
                    </div>
                    <div className="flex flex-column gap-1">
                        <label htmlFor="coupon-type">Discount type</label>
                        <Dropdown
                            id="coupon-type"
                            value={form.discount_type}
                            options={DISCOUNT_TYPES}
                            onChange={(e) => setForm((f) => ({ ...f, discount_type: e.value }))}
                            className="w-full"
                        />
                    </div>
                    <div className="flex flex-column gap-1">
                        <label htmlFor="coupon-value">
                            Discount value {form.discount_type === "percentage" ? "(%)" : "(CAD flat)"}
                        </label>
                        <InputNumber
                            id="coupon-value"
                            value={form.discount_value}
                            onValueChange={(e) => setForm((f) => ({ ...f, discount_value: e.value }))}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            min={0.01}
                            className="w-full"
                        />
                    </div>
                    <div className="flex align-items-center gap-2">
                        <Checkbox
                            inputId="upto-unlimited"
                            checked={form.upto_unlimited}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    upto_unlimited: e.checked,
                                    upto_rides: e.checked ? null : f.upto_rides || 3,
                                }))
                            }
                        />
                        <label htmlFor="upto-unlimited">Unlimited rides (until end date)</label>
                    </div>
                    {!form.upto_unlimited && (
                        <div className="flex flex-column gap-1">
                            <label htmlFor="upto-rides">Valid up to ride number</label>
                            <InputNumber
                                id="upto-rides"
                                value={form.upto_rides}
                                onValueChange={(e) => setForm((f) => ({ ...f, upto_rides: e.value }))}
                                min={1}
                                showButtons
                                className="w-full"
                            />
                            <small className="text-600">
                                Example: 3 means only the passenger&apos;s 1st–3rd completed trips can use this code.
                            </small>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
